import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Smile, Shield, Activity, AlignCenter, Sun,
  Sparkles, Layers, Zap, Star, Ambulance, Bone,
  ChevronRight, Leaf, ArrowRight,
} from 'lucide-react';
import { LeafCluster } from '../../components/WoodlandSVG';
import { fadeLeft, fadeRight, fadeUp, pageTransition, viewportConfig } from '../../components/AnimationVariants';

const PUB = process.env.PUBLIC_URL;

export const TREATMENTS = [
  {
    slug: 'general-dentistry',
    icon: Smile,
    title: 'General Dentistry',
    tagline: 'The foundation of a healthy smile',
    photo: `${PUB}/images/treatments/dental-examinations.png`,
    heroPhoto: `${PUB}/images/treatments/hero/general-dentistry.jpg`,
    color: 'from-forest to-forest-light',
    accent: '#1B3A2D',
    intro: 'We believe great dentistry starts with understanding. Our comprehensive examinations provide a clear and accurate assessment of your oral health, helping you make informed decisions with confidence.',
    sections: [
      {
        heading: 'Dental Examinations',
        image: `${PUB}/images/treatments/sections/dental-examination.jpg`,
        body: 'Using advanced digital technology — including high-definition intraoral cameras and low-dose digital X-rays — we can detect concerns at an early stage and clearly explain our findings. When required, 3D CBCT imaging allows us to assess teeth, bone and surrounding structures with exceptional precision for treatments such as implants and complex restorative care. Our aim is simple: to provide accurate diagnosis, honest advice and personalised care to help you maintain a healthy smile for years to come.',
      },
      {
        heading: 'Hygiene & Gum Care',
        image: `${PUB}/images/treatments/sections/hygiene-airflow.jpg`,
        body: 'Healthy gums are the foundation of a healthy smile. Our hygiene appointments focus on preventing dental problems before they arise, helping to keep your teeth and gums in excellent condition. Using the advanced OPUS® air polishing and ultrasonic system, we gently remove plaque, biofilm and staining while preserving tooth enamel and soft tissues. This minimally invasive approach offers a more comfortable experience, particularly for patients with sensitive teeth.',
      },
      {
        heading: 'Crowns & Bridges',
        image: `${PUB}/images/treatments/sections/crown-bridge.jpg`,
        body: 'Crowns and bridges are reliable, long-lasting solutions for restoring damaged or missing teeth. Whether strengthening a heavily restored tooth or replacing a missing tooth, each restoration is carefully designed to restore function, comfort and appearance. Using high-quality materials and modern digital techniques, we create natural-looking restorations that blend seamlessly with your smile.',
      },
    ],
    features: ['Comprehensive Exams', 'Professional Cleanings', 'Digital X-Rays', 'Intraoral Camera', 'Crowns & Bridges', 'OPUS® Hygiene System'],
  },
  {
    slug: 'restorative-dentistry',
    icon: Shield,
    title: 'Restorative Dentistry',
    tagline: 'Rebuild, repair, and restore',
    photo: `${PUB}/images/treatments/dental-crowns.png`,
    heroPhoto: `${PUB}/images/treatments/hero/restorative-dentistry.jpg`,
    color: 'from-sage to-sage-light',
    accent: '#87A96B',
    intro: 'From simple fillings to full-mouth restorations, we rebuild the function and appearance of damaged or missing teeth using the latest materials and techniques — helping you eat, speak, and smile with confidence.',
    sections: [
      {
        heading: 'Tooth-Coloured Fillings',
        image: `${PUB}/images/treatments/sections/tooth-filling.jpg`,
        body: 'Our natural-looking tooth-coloured fillings are designed to restore strength, function and appearance while blending seamlessly with your smile. Using modern adhesive materials and minimally invasive techniques, we remove only the damaged portion of the tooth, preserving as much healthy tooth structure as possible. Carefully matched to the shade of your natural teeth, our fillings provide a durable and aesthetically pleasing solution for treating decay, chips and minor wear.',
      },
      {
        heading: 'Inlays & Onlays',
        image: `${PUB}/images/treatments/sections/ceramic-inlay.jpg`,
        body: 'Inlays and onlays are precision-crafted restorations that offer a conservative alternative to full crowns when a tooth requires more protection than a conventional filling can provide. Custom-made to fit your tooth with exceptional accuracy, they reinforce weakened areas, restore natural contours and withstand the forces of everyday chewing. Crafted from high-quality ceramic materials, they blend beautifully with your natural teeth, delivering outstanding aesthetics alongside long-term durability.',
      },
      {
        heading: 'Crowns',
        image: `${PUB}/images/treatments/sections/dental-crown.jpg`,
        body: 'Our custom-crafted dental crowns are designed to restore teeth that have been weakened by decay, fractures or extensive dental treatment. Combining strength with natural aesthetics, crowns provide full coverage protection while recreating the shape, function and appearance of a healthy tooth. Each crown is individually designed to complement your smile, delivering a restoration that is durable, comfortable and beautifully lifelike.',
      },
      {
        heading: 'Bridges',
        image: `${PUB}/images/treatments/sections/bridge-restoration.jpg`,
        body: 'Dental bridges provide an elegant and long-lasting solution for replacing missing teeth, restoring both the appearance and function of your smile. For front teeth, a carefully crafted bridge can enhance aesthetics and confidence by seamlessly recreating a natural-looking smile. For back teeth, bridges help restore chewing efficiency and support a balanced bite. Custom-made using high-quality materials, each bridge is designed to blend harmoniously with your natural teeth.',
      },
    ],
    features: ['Tooth-Coloured Fillings', 'Inlays & Onlays', 'Dental Crowns', 'Bridges', 'Full-Mouth Rehabilitation', 'Minimal Intervention'],
  },
  {
    slug: 'root-canal-treatments',
    icon: Activity,
    title: 'Root Canal Treatments',
    tagline: 'Save your tooth, end the pain',
    photo: `${PUB}/images/treatments/root-canal.png`,
    heroPhoto: `${PUB}/images/treatments/hero/root-canal.jpg`,
    color: 'from-sage-light to-sage-pale',
    accent: '#A8C48A',
    intro: 'Root canal treatment is often the best way to save a tooth that would otherwise require extraction. By carefully removing infection from within the tooth and sealing the root canal system, we can relieve discomfort, preserve your natural tooth and restore long-term function.',
    sections: [
      {
        heading: 'Microscope-Assisted Endodontics',
        image: `${PUB}/images/treatments/sections/endodontics-microscope.jpg`,
        body: 'Our dentist with a special interest in endodontics combines advanced training with modern technology to deliver precise and predictable treatment outcomes. Using an advanced dental microscope, we are able to visualise the intricate internal anatomy of the tooth under high magnification, allowing us to identify fine canals, assess complex cases and carry out treatment with exceptional accuracy.',
      },
      {
        heading: 'Our Approach',
        image: `${PUB}/images/treatments/sections/root-canal-approach.jpg`,
        body: 'Together with digital imaging and meticulous clinical techniques, this enhanced level of precision supports a conservative approach focused on preserving your natural tooth whenever possible. With modern techniques and gentle anaesthesia, root canals are no more uncomfortable than a standard filling. Our goal is simple: to eliminate infection, restore comfort and help you retain your natural smile for many years to come.',
      },
    ],
    features: ['Painless Technique', 'Dental Microscope', 'Digital Imaging', 'Same-Day Treatment', 'Conservative Approach', 'Follow-Up Crown'],
  },
  {
    slug: 'invisalign',
    icon: AlignCenter,
    title: 'Invisalign®',
    tagline: 'Straighten your smile discreetly',
    photo: `${PUB}/images/treatments/invisalign-clearcorrect.png`,
    heroPhoto: `${PUB}/images/treatments/hero/invisalign.jpg`,
    color: 'from-forest-dark to-forest',
    accent: '#122619',
    intro: 'Invisalign uses a series of clear, removable aligners to gradually move teeth into a better position. The aligners are discreet, comfortable and designed to fit around everyday life — because they can be removed for eating, brushing and flossing.',
    sections: [
      {
        heading: 'Invisalign® & ClearCorrect®',
        image: `${PUB}/images/treatments/sections/clear-aligner.jpg`,
        body: 'We offer treatment with leading clear aligner systems, including Invisalign® and ClearCorrect®, planned and overseen by our Specialist Orthodontist. These virtually invisible aligners provide a comfortable, effective and discreet solution for achieving a beautifully aligned smile. Your treatment begins with digital scans and careful planning — you will be shown how your smile is expected to move before any aligners are made.',
      },
      {
        heading: 'Orthodontics for Adults',
        image: `${PUB}/images/treatments/sections/adult-orthodontics.jpg`,
        body: 'Orthodontic treatment is not just for children. Our Specialist Orthodontist offers tailored solutions for adults looking to improve the appearance, alignment and function of their smile. We also offer traditional fixed braces, ceramic braces and lingual braces — your clinician will recommend the most suitable option after assessing your teeth, bite, goals and treatment complexity.',
      },
      {
        heading: 'Orthodontics for Children',
        image: `${PUB}/images/treatments/sections/orthodontics-children.jpg`,
        body: 'Our Specialist Orthodontist provides expert assessment and treatment for children, helping to guide dental development, create space for erupting teeth and address orthodontic concerns at the most appropriate stage of growth. We also offer Invisalign First for younger patients with a mix of baby and adult teeth.',
      },
      {
        heading: 'Retainers',
        image: `${PUB}/images/treatments/sections/retainer.jpg`,
        body: 'Retainers are essential after orthodontic treatment. Teeth can naturally move over time, so retainers help protect the smile you have worked hard to achieve. We will recommend the right retention plan for you, which may include removable retainers, fixed bonded retainers, or a combination of both.',
      },
    ],
    features: ['Invisalign® Clear Aligners', 'ClearCorrect®', 'Fixed & Ceramic Braces', 'Lingual Braces', 'Children & Adults', 'Retainers Included'],
  },
  {
    slug: 'teeth-whitening',
    icon: Sun,
    title: 'Teeth Whitening',
    tagline: 'Brighter smile, one visit',
    photo: `${PUB}/images/treatments/teeth-whitening.png`,
    heroPhoto: `${PUB}/images/treatments/hero/teeth-whitening.jpg`,
    color: 'from-gold to-gold-light',
    accent: '#C5A55A',
    intro: 'A brighter smile can make a remarkable difference to your confidence. Our professional teeth whitening treatments are carefully tailored to enhance the natural brightness of your teeth, lifting stains and discolouration while maintaining a natural-looking result.',
    sections: [
      {
        heading: 'Professional Whitening',
        image: `${PUB}/images/treatments/sections/whitening-treatment.jpg`,
        body: 'Using high-quality professional whitening systems, we provide safe, predictable and effective treatment designed around your individual goals. The result is a fresher, more youthful smile that complements your natural features rather than appearing artificially white. Custom take-home kits allow you to whiten gradually and comfortably at your own pace.',
      },
      {
        heading: 'Non-Vital Tooth Whitening',
        image: `${PUB}/images/treatments/sections/single-tooth-whitening.jpg`,
        body: 'A single tooth can sometimes become darker following trauma or root canal treatment, creating a noticeable difference in your smile. Non-vital tooth whitening is a conservative treatment designed to brighten a discoloured tooth from within, helping it blend more naturally with the surrounding teeth. By placing a whitening agent inside the tooth, we can gradually restore its appearance without removing healthy tooth structure or placing unnecessary restorations.',
      },
    ],
    features: ['Professional Whitening Systems', 'Custom Take-Home Kits', 'Non-Vital Whitening', 'Sensitive-Tooth Formula', 'Shade Assessment', 'Natural-Looking Results'],
  },
  {
    slug: 'porcelain-veneers',
    icon: Sparkles,
    title: 'Porcelain Veneers',
    tagline: 'Hollywood smile, naturally yours',
    photo: `${PUB}/images/treatments/porcelain-veneers.png`,
    heroPhoto: `${PUB}/images/treatments/hero/porcelain-veneers.jpg`,
    color: 'from-gold-dark to-gold',
    accent: '#C5A55A',
    intro: 'Porcelain veneers are ultra-thin, custom-crafted restorations designed to enhance the shape, colour and overall appearance of your teeth while maintaining a natural and harmonious smile.',
    sections: [
      {
        heading: 'Emax Porcelain Veneers',
        image: `${PUB}/images/treatments/sections/veneer-procedure.jpg`,
        body: 'We offer premium Emax veneers, renowned for their exceptional strength, lifelike translucency and natural aesthetics. Carefully designed to complement your facial features and smile, Emax veneers reflect light in a way that closely mimics natural enamel, creating beautifully refined and long-lasting results. They can be used to improve discolouration, worn edges, minor misalignment, uneven spacing and other cosmetic concerns.',
      },
      {
        heading: 'Our Approach',
        image: `${PUB}/images/treatments/sections/veneer-consultation.jpg`,
        body: 'Every veneer treatment is individually planned with a conservative approach, focusing on achieving elegant, natural-looking improvements while preserving as much healthy tooth structure as possible. We take time to understand your aesthetic goals and design your veneers to complement your facial features — the result should look like a naturally beautiful version of you.',
      },
    ],
    features: ['Emax Porcelain Veneers', 'Custom Design Consultation', 'Minimal Tooth Preparation', 'Stain Resistant', 'Long-Lasting Results', 'Natural Appearance'],
  },
  {
    slug: 'composite-bonding',
    icon: Layers,
    title: 'Composite Bonding',
    tagline: 'Quick, affordable smile enhancement',
    photo: `${PUB}/images/treatments/composite-bonding.png`,
    heroPhoto: `${PUB}/images/treatments/hero/composite-bonding.jpg`,
    color: 'from-brown to-brown-light',
    accent: '#6B4226',
    intro: 'Composite bonding is a versatile and minimally invasive treatment designed to enhance the appearance of your smile while preserving your natural teeth.',
    sections: [
      {
        heading: 'Natural-Looking Results',
        image: `${PUB}/images/treatments/sections/bonding-procedure.jpg`,
        body: 'Using premium tooth-coloured materials, we can refine tooth shape, repair chips, close small gaps and improve overall smile aesthetics with beautiful, natural-looking results. Each treatment is carefully customised to complement your individual features, with meticulous attention to detail ensuring a seamless blend with your existing teeth.',
      },
      {
        heading: 'Conservative & Reversible',
        image: `${PUB}/images/treatments/sections/composite-bonding-smile.jpg`,
        body: 'Often requiring little to no removal of healthy tooth structure, composite bonding offers a conservative approach to smile enhancement. Whether you are seeking subtle improvements or a more noticeable transformation, composite bonding provides an effective way to achieve a balanced, confident and naturally attractive smile — frequently in a single appointment with no anaesthetic required.',
      },
    ],
    features: ['Single-Visit Treatment', 'No Injections Required', 'Chips & Cracks Repaired', 'Gap Closure', 'Natural Colour Match', 'Reversible Procedure'],
  },
  {
    slug: 'dental-implants',
    icon: Zap,
    title: 'Dental Implants',
    tagline: 'Permanent. Natural. Life-changing.',
    photo: `${PUB}/images/treatments/dental-implants.png`,
    heroPhoto: `${PUB}/images/treatments/hero/dental-implants.jpg`,
    color: 'from-forest to-forest-light',
    accent: '#1B3A2D',
    intro: 'Dental implants are the closest replacement to a natural tooth, restoring not only your smile but also the strength, stability and confidence that can be lost when a tooth is missing.',
    sections: [
      {
        heading: 'Advanced 3D Planning',
        image: `${PUB}/images/treatments/sections/cbct-planning.jpg`,
        body: 'Unlike traditional options, implants are placed directly into the jawbone, creating a secure foundation for a custom-made crown, bridge or denture. Every implant treatment begins with meticulous planning using advanced 3D CBCT imaging, allowing us to assess bone quality and anatomy with exceptional precision. This digital approach enables highly accurate implant placement and predictable long-term outcomes.',
      },
      {
        heading: 'Natural Function & Appearance',
        image: `${PUB}/images/treatments/sections/implant-placement.jpg`,
        body: 'This helps preserve bone levels, maintain facial support and restore natural chewing function without relying on neighbouring teeth. Designed to look, feel and function like natural teeth, dental implants offer a durable and elegant solution for replacing missing teeth — helping you eat, smile and speak with complete confidence.',
      },
    ],
    features: ['Single Tooth Implants', 'Implant-Supported Bridges', 'All-on-4 / All-on-6', '3D CBCT Planning', 'Same-Day Temporaries', 'Bone Grafting'],
  },
  {
    slug: 'facial-aesthetics',
    icon: Star,
    title: 'Facial Aesthetics',
    tagline: 'Enhance your natural beauty',
    photo: `${PUB}/images/treatments/anti-wrinkle.png`,
    heroPhoto: `${PUB}/images/treatments/hero/facial-aesthetics.jpg`,
    color: 'from-brown-light to-brown-pale',
    accent: '#8B5E3C',
    intro: 'Complement your smile with our range of facial aesthetic treatments delivered by experienced clinicians with a detailed understanding of facial anatomy.',
    sections: [
      {
        heading: 'Anti-Wrinkle Treatment',
        image: `${PUB}/images/treatments/sections/anti-wrinkle-injection.jpg`,
        body: 'Anti-wrinkle treatments offer a simple and effective way to soften the appearance of fine lines and expression wrinkles, helping you achieve a fresher, more rested appearance while maintaining natural facial movement. Our approach focuses on subtle, natural-looking enhancements that refresh and rejuvenate without creating an over-treated appearance. Whether you are looking to smooth forehead lines, frown lines or crow\'s feet, anti-wrinkle treatment can help restore a more youthful and confident appearance with minimal downtime.',
      },
      {
        heading: 'Lip Enhancement',
        image: `${PUB}/images/treatments/sections/lip-filler.jpg`,
        body: 'Lip enhancement treatments are designed to restore volume, improve definition and create natural-looking balance and harmony within the smile and facial profile. Using premium dermal fillers, treatment is carefully tailored to your individual features and desired outcome. Our approach focuses on achieving soft, elegant and natural-looking results that complement your facial features rather than overpower them.',
      },
      {
        heading: 'Jaw Muscle Relaxation (TMJ)',
        image: `${PUB}/images/treatments/sections/jaw-relaxation.jpg`,
        body: 'Jaw clenching and teeth grinding can place excessive strain on the jaw muscles and temporomandibular joints, often leading to jaw pain, headaches, facial tension and tooth wear. Using carefully administered muscle-relaxing treatment, we can help reduce the activity of overactive jaw muscles, relieving tension and improving comfort — helping to protect your teeth from the long-term effects of excessive grinding.',
      },
    ],
    features: ['Anti-Wrinkle Injections', 'Dermal Fillers', 'Lip Enhancement', 'Gummy Smile Correction', 'Jaw Muscle Relaxation', 'Natural-Looking Results'],
  },
  {
    slug: 'dental-emergencies',
    icon: Ambulance,
    title: 'Dental Emergencies',
    tagline: "We're here when it matters most",
    photo: `${PUB}/images/treatments/periodontal-gum.png`,
    heroPhoto: `${PUB}/images/treatments/hero/dental-emergencies.jpg`,
    color: 'from-forest-light to-sage',
    accent: '#2D5A3D',
    intro: 'Dental pain and emergencies rarely happen at a convenient time. Whether you are suffering from severe toothache, swelling, a broken tooth, trauma or a lost restoration, we understand how distressing and disruptive these situations can be.',
    sections: [
      {
        heading: 'Same-Day Emergency Care',
        image: `${PUB}/images/treatments/sections/emergency-dental.jpg`,
        body: 'Our priority is to see you as quickly as possible, relieve your discomfort and provide clear guidance on the best course of treatment. We take the time to listen, assess the problem thoroughly and ensure you feel supported throughout your visit. When you are in pain, you need more than just treatment — you need reassurance, understanding and prompt care.',
      },
      {
        heading: 'Periodontal (Gum) Treatment',
        image: `${PUB}/images/treatments/sections/gum-treatment.jpg`,
        body: 'Gum disease can develop silently and may cause bleeding gums, bad breath, gum recession, looseness and eventual tooth loss if untreated. Our periodontal care, provided by a dentist with a special interest in periodontics, focuses on diagnosis, stabilisation and long-term maintenance. Treatment may include detailed gum assessment, deep cleaning, personalised home care advice and ongoing review.',
      },
    ],
    features: ['Same-Day Appointments', 'Toothache Relief', 'Broken Tooth Repair', 'Lost Filling / Crown', 'Dental Abscess', 'Periodontal Care'],
  },
  {
    slug: 'tmj-disorder',
    icon: Bone,
    title: 'TMJ Disorder',
    tagline: 'Relief from jaw pain and headaches',
    photo: `${PUB}/images/treatments/tmj-disorder.png`,
    heroPhoto: `${PUB}/images/treatments/hero/tmj-disorder.jpg`,
    color: 'from-sage to-forest',
    accent: '#87A96B',
    intro: 'TMJ (temporomandibular joint) disorder can cause jaw pain, clicking, persistent headaches, and difficulty chewing. We offer thorough assessment and tailored treatments to restore comfortable, pain-free jaw function.',
    sections: [
      {
        heading: 'Assessment & Diagnosis',
        image: `${PUB}/images/treatments/sections/tmj-assessment.jpg`,
        body: 'The temporomandibular joints connect your jawbone to your skull and play a vital role in everyday activities such as speaking, chewing and yawning. When these joints are under strain — often due to clenching, grinding or bite imbalance — the resulting discomfort can significantly affect your quality of life. We begin with a thorough assessment to understand the cause and severity of your symptoms.',
      },
      {
        heading: 'Treatment Options',
        image: `${PUB}/images/treatments/sections/tmj-splint.jpg`,
        body: 'Treatment is tailored to each individual and may include occlusal splints or night guards to reduce grinding and protect the teeth, bite adjustment, or physiotherapy referral for muscle relaxation. For patients where muscle activity is the primary concern, carefully administered muscle-relaxing treatment can be highly effective in reducing jaw tension and associated headaches, helping to protect your teeth from the long-term effects of excessive grinding and muscle strain.',
      },
    ],
    features: ['Jaw Pain Assessment', 'Occlusal Splints', 'Night Guards', 'Physiotherapy Referrals', 'Bite Adjustment', 'Ongoing Monitoring'],
  },
];

export const TREATMENT_NAV_ITEMS = TREATMENTS.map(t => ({
  label: t.title,
  to: `/services/${t.slug}`,
}));

export function TreatmentSidebar() {
  const { pathname } = useLocation();
  return (
    <motion.div className="bg-white rounded-2xl p-6 shadow-sm border border-cream-deeper lg:sticky lg:top-28"
      variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
      <h3 className="font-serif text-forest text-lg font-semibold mb-1">All Treatments</h3>
      <Link to="/services" className="text-xs text-gray-400 hover:text-forest transition-colors mb-4 block">
        View overview →
      </Link>
      <div className="space-y-1">
        {TREATMENTS.map((t) => {
          const Icon = t.icon;
          const active = pathname === `/services/${t.slug}`;
          return (
            <Link key={t.slug} to={`/services/${t.slug}`}
              aria-current={active ? "page" : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest ${
                active ? 'bg-forest text-white' : 'text-gray-600 hover:bg-sage-pale hover:text-forest'
              }`}>
              <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-white' : 'text-forest/60'}`} />
              <span className="font-medium leading-tight">{t.title}</span>
              <ChevronRight className={`w-3.5 h-3.5 ml-auto flex-shrink-0 ${active ? 'text-white' : 'text-gray-300'}`} />
            </Link>
          );
        })}
      </div>
      <div className="mt-5 p-4 bg-sage-pale rounded-xl">
        <p className="text-xs text-forest/80 leading-relaxed">
          <strong>Ready to book?</strong><br />
          Call <a href="tel:+442088665556" className="underline">020 8866 5556</a> or{' '}
          <Link to="/contact" className="underline">book online</Link>.
        </p>
      </div>
    </motion.div>
  );
}

export function TreatmentPageWrapper({ treatment, children }) {
  return (
    <motion.div {...pageTransition} className="pt-24">
      {/* Hero */}
      <section className={`relative py-20 px-4 bg-gradient-to-br ${treatment.color} overflow-hidden`}>
        <motion.div className="absolute right-0 top-0 w-64 opacity-10 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-white/70 text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Our Treatments
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-4"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            {treatment.title}
          </motion.h1>
          <motion.p className="text-white/75 text-lg italic leading-relaxed mb-6"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            {treatment.tagline}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
            <Link to="/contact" className="btn-primary text-sm">
              Book a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1 order-2 lg:order-1">
              <TreatmentSidebar />
            </div>
            <motion.div className="lg:col-span-3 order-1 lg:order-2"
              variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              {children}
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
